import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { LOGO_URL, BRAND } from "./Header";
import { GOOGLE_REVIEW_URL } from "../lib/links";
import { useSiteContent } from "../contexts/SiteContentContext";
import { openCookieConsent } from "./CookieConsent";

export default function Footer() {
  const { c } = useSiteContent();
  const addressLines = c("contact_address", "4 rue de la Laiterie, 27400 Louviers — Normandie").split(",").map(s => s.trim());
  const hoursLines = c("contact_hours", "Mardi — Samedi · 17h — 23h\nDimanche & Lundi · privatisation sur demande").split("\n");
  return (
    <footer className="border-t border-border mt-32" data-testid="site-footer">
      {/* Signature quote — reprises l'esprit "Good Drinks · Good Music · Better People" de la scène DJ */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-4 text-center">
        <div className="kintsugi-divider mb-8" />
        <p className="cursive-quote" data-testid="footer-mantra">
          Good Drinks · Good Music · Better People
        </p>
        <div className="mt-6 flex items-center justify-center gap-6 text-lg font-display tracking-[0.3em]">
          <span className="gold-text">DÉGUSTER</span>
          <span className="w-1 h-1 rounded-full bg-primary/60" />
          <span className="gold-text">PARTAGER</span>
        </div>
        <div className="kintsugi-divider mt-8" />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt={BRAND} className="h-14 w-14 rounded-full object-cover bg-black" data-testid="footer-logo" />
            <div className="font-display text-2xl leading-none">{BRAND}</div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Bar à cocktails à Louviers (Normandie) : bières artisanales locales, cocktails signatures, rhums et calvados arrangés primés au Frankfurt Trophy. Ateliers de mixologie et bar mobile pour mariages, EVG et événements privés.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Visiter</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/evenements" className="hover:text-primary">Événements</Link></li>
            <li><Link to="/reservation-table" className="hover:text-primary">Réserver une table</Link></li>
            <li><Link to="/evenement-prive" className="hover:text-primary">Bar mobile</Link></li>
            <li><Link to="/carte" className="hover:text-primary">Carte des cocktails</Link></li>
            <li><Link to="/boutique" className="hover:text-primary">Boutique — rhums arrangés</Link></li>
            <li><Link to="/cartes-cadeaux" className="hover:text-primary">Cartes cadeaux</Link></li>
            <li><Link to="/mon-histoire" className="hover:text-primary" data-testid="footer-story-link">Mon histoire — Éric Awono</Link></li>
            <li>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:underline"
                data-testid="footer-google-review-link"
              >
                <Star className="w-3.5 h-3.5 fill-primary" />
                Laisser un avis Google
              </a>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Contact</div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {addressLines.map((line, i) => <li key={`addr-${i}`}>{line}</li>)}
            <li>e.awono@saveursnormandes.fr</li>
            <li>{c("contact_phone_display", "06 65 02 87 00")}</li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Horaires</div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {hoursLines.map((line, i) => <li key={`hour-${i}`}>{line}</li>)}
          </ul>
        </div>
      </div>
      <div className="border-t border-border py-6 px-6 lg:px-10 text-xs text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-3">
        <div>© {new Date().getFullYear()} {BRAND} — Tous droits réservés · SIRET 990 061 350 00015 · Licence IV</div>
        <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-legal-nav">
          <Link to="/mentions-legales" className="hover:text-primary" data-testid="footer-mentions-legales">Mentions légales</Link>
          <span className="opacity-30">·</span>
          <Link to="/cgv" className="hover:text-primary" data-testid="footer-cgv">CGV</Link>
          <span className="opacity-30">·</span>
          <Link to="/confidentialite" className="hover:text-primary" data-testid="footer-confidentialite">Confidentialité</Link>
          <span className="opacity-30">·</span>
          <button
            type="button"
            onClick={openCookieConsent}
            className="hover:text-primary underline-offset-2 hover:underline"
            data-testid="footer-cookies"
          >
            Cookies
          </button>
        </nav>
      </div>
      <div className="border-t border-border py-3 text-center text-[11px] text-muted-foreground/70 px-6">
        L'abus d'alcool est dangereux pour la santé. À consommer avec modération. Vente interdite aux mineurs de moins de 18 ans.
      </div>
    </footer>
  );
}
